import React, { useState } from 'react';
import { PLES, getPLELogo } from './plesDatabase';

const css = `
  * { box-sizing: border-box; }
  html { font-size: 133%; }
  .ple-card { transition: background 0.18s, border-color 0.18s, transform 0.18s; cursor: pointer; }
  .ple-card:hover { transform: scale(1.06); filter: brightness(1.1) !important; }
  .ple-card.selected { outline: 2px solid rgba(212,175,55,0.9) !important; }
  .btn-wwe:hover { background: rgba(255,255,255,0.1) !important; border-color: rgba(255,255,255,0.6) !important; color: #fff !important; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.2); border-radius: 2px; }
`;

const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono: { fontFamily: "'Monda', sans-serif" },
};

export default function WeekStartPopup({ gameData, onStartWeek }) {
  const season = gameData?.season || 1;
  const budget = gameData?.budget || 3000000;
  const fans = gameData?.fans ?? 1000000;
  const brand = gameData?.brand || 'Raw';
  const brandColor = gameData?.brandColor || '#d4af37';
  const brandLogo = gameData?.brandLogo || null;
  const brandBg = gameData?.brandBg || null;
  const week = gameData?.week || 1;
  const isPLEWeek = week % 5 === 0;
  const isGoHomeWeek = week % 5 === 4;
  const nextPleId = gameData?.nextPle || null;

  // Background selon le type de semaine
  var weekBg = brandBg;
  var weekBgFilter = 'blur(12px) brightness(0.3)';
  var weekAccentColor = brandColor;
  if (isPLEWeek && nextPleId) {
    weekBg = process.env.PUBLIC_URL + '/ples/' + nextPleId + '_bg.png';
    weekBgFilter = 'blur(14px) brightness(0.25)';
    // PLE color from theme — use brandColor as fallback
    const PLE_COLORS = { 'wrestlemania': '#d4af37', 'summerslam': '#e67e22', 'survivor-series': '#e74c3c', 'elimination-chamber': '#95a5a6', 'money-in-the-bank': '#2ecc71', 'hell-in-a-cell': '#c0392b', 'backlash': '#922b21', 'night-of-champions': '#d4af37', 'clash-in-paris': '#1a6bb5', 'crown-jewel': '#9b59b6', 'battleground': '#7f8c8d', 'evolution': '#d4af37', 'great-american-bash': '#e74c3c', 'halloween-havoc': '#e67e22', 'no-mercy': '#2c3e50', 'worlds-collide': '#16a085' };
    weekAccentColor = PLE_COLORS[nextPleId] || brandColor;
  } else if (isGoHomeWeek) {
    weekAccentColor = '#e67e22';
    weekBgFilter = 'blur(16px) brightness(0.2)';
  }

  // PLE picking allowed only on week 1 post-PLE (week % 5 === 1) or week 1
  const isWrestleManiaBlock = week >= 46;
  const canChangePLE = !isWrestleManiaBlock && (week === 1 || week % 5 === 1);
  const [selectedPLE, setSelectedPLE] = useState(gameData?.nextPle || null);
  const [showPLEPicker, setShowPLEPicker] = useState(false);
  const [editedBudget, setEditedBudget] = useState(budget);
  const [editingBudget, setEditingBudget] = useState(false);

  const nextPLEWeek = Math.ceil(week / 5) * 5;
  var rivals = (gameData && gameData.rivals || []).filter(Boolean);
  var rankingEntries = [{ brand: gameData && gameData.brand, fans, isMe: true }]
    .concat(rivals.map(function(r) { return { brand: r.brand, fans: r.fans || 0, isMe: false }; }))
    .sort(function(a, b) { return b.fans - a.fans; });
  var myRank = rankingEntries.findIndex(function(e) { return e.isMe; }) + 1;
  var totalBrands = rankingEntries.length;
  const weeksLeft = nextPLEWeek - week;

  // Force WrestleMania for weeks 46-50
  var effectivePLE = isWrestleManiaBlock ? 'wrestlemania' : selectedPLE;
  const selectedPLEData = PLES.find(p => p.id === effectivePLE);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{css}</style>

      {/* BG selon type de semaine */}
      {weekBg
        ? <img loading="lazy" src={weekBg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: weekBgFilter, zIndex: 0 }} onError={e => { e.target.style.display='none'; }} />
        : <div style={{ position: 'absolute', inset: 0, background: '#0a0a0a', zIndex: 0 }} />
      }
      {/* Go-Home grid overlay */}
      {isGoHomeWeek && <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(230,126,34,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(230,126,34,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', zIndex: 1, pointerEvents: 'none' }} />}
      {/* Color glow */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, ' + weekAccentColor + '18 0%, rgba(0,0,0,0.75) 100%)', zIndex: 1 }} />

      {/* Popup card */}
      <div style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: '620px', background: 'rgba(6,4,0,0.92)', border: '1px solid ' + weekAccentColor + '44', borderRadius: '16px', backdropFilter: 'blur(6px)', boxShadow: '0 30px 100px rgba(0,0,0,0.95), 0 0 60px ' + weekAccentColor + '22', overflow: 'hidden' }}>

        {/* Top accent */}
        <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, ' + weekAccentColor + ', transparent)' }} />

        <div style={{ padding: '32px 36px' }}>
          {/* Brand logo */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            {brandLogo
              ? <img loading="lazy" src={brandLogo} alt={brand} style={{ height: '80px', objectFit: 'contain', filter: 'drop-shadow(0 0 20px ' + brandColor + '88)' }} />
              : <div style={{ ...S.anton, fontSize: '2.5rem', letterSpacing: '4px', color: brandColor, textShadow: '0 0 30px ' + brandColor + '88' }}>{brand.toUpperCase()}</div>
            }
          </div>

          {/* Season + Week */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ ...S.mono, fontSize: '0.6rem', letterSpacing: '3px', color: `${brandColor}88`, marginBottom: '6px' }}>
              SEASON {season}
            </div>
            <div style={{ ...S.anton, fontSize: '3rem', letterSpacing: '4px', color: '#fff', lineHeight: 1, textShadow: '0 0 30px rgba(255,255,255,0.2)' }}>
              WEEK <span style={{ color: brandColor }}>{week}</span>
            </div>
          </div>

          {/* Budget + Fans */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '24px' }}>
            {/* Budget — editable on week 1 */}
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid ' + (editingBudget ? 'rgba(46,204,113,0.5)' : 'rgba(255,255,255,0.08)'), borderRadius: '8px', padding: '12px 16px', textAlign: 'center', position: 'relative' }}>
              <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '2px', marginBottom: '5px' }}>BUDGET</div>
              {editingBudget ? (
                <input
                  type="number"
                  value={editedBudget}
                  onChange={e => setEditedBudget(Number(e.target.value))}
                  onBlur={() => setEditingBudget(false)}
                  autoFocus
                  style={{ background: 'transparent', border: 'none', outline: 'none', fontFamily: "'Anton',sans-serif", fontSize: '1rem', letterSpacing: '2px', color: '#2ecc71', textAlign: 'center', width: '100%' }}
                />
              ) : (
                <div style={{ ...S.anton, fontSize: '1.1rem', letterSpacing: '2px', color: '#2ecc71', cursor: week === 1 ? 'pointer' : 'default' }}
                  onClick={() => week === 1 && setEditingBudget(true)}>
                  ${Math.round(editedBudget).toLocaleString('en-US')}
                </div>
              )}
              {week === 1 && !editingBudget && (
                <div style={{ ...S.mono, fontSize: '0.36rem', color: 'rgba(255,255,255,0.2)', marginTop: '3px', letterSpacing: '1px' }}>✏ CLICK TO EDIT</div>
              )}
            </div>
            {/* Fans */}
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '12px 16px', textAlign: 'center' }}>
              <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '2px', marginBottom: '5px' }}>FANS</div>
              <div style={{ ...S.anton, fontSize: '1.1rem', letterSpacing: '2px', color: '#3498db' }}>{fans.toLocaleString('en-US')}</div>
            </div>
            {/* Ranking */}
            <div style={{ background: weekAccentColor + '08', border: '1px solid ' + weekAccentColor + '33', borderRadius: '8px', padding: '12px 16px', textAlign: 'center' }}>
              <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '2px', marginBottom: '5px' }}>CLASSEMENT</div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '4px' }}>
                <div style={{ ...S.anton, fontSize: '1.4rem', letterSpacing: '2px', color: weekAccentColor }}>{myRank}</div>
                <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)' }}>/ {totalBrands}</div>
              </div>
            </div>
          </div>

          {/* Next PLE */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ ...S.mono, fontSize: '0.5rem', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)', marginBottom: '10px' }}>
              NEXT PLE {isWrestleManiaBlock ? '— WRESTLEMANIA SEASON' : (!canChangePLE && '— ' + weeksLeft + ' WEEK' + (weeksLeft > 1 ? 'S' : '') + ' LEFT')}
            </div>

            {/* PLE zone - clickable if canChangePLE */}
            <div
              onClick={() => canChangePLE && setShowPLEPicker(true)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.03)', border: '1px solid ' + (canChangePLE ? (selectedPLEData ? brandColor + '55' : 'rgba(255,255,255,0.15)') : 'rgba(255,255,255,0.06)'), borderRadius: '10px', padding: '18px', cursor: canChangePLE ? 'pointer' : 'default', transition: 'background 0.18s, border-color 0.18s, color 0.18s', minHeight: '100px', justifyContent: 'center' }}>
              {selectedPLEData ? (
                <>
                  <img loading="lazy" src={getPLELogo(selectedPLEData.id)} alt={selectedPLEData.name}
                    style={{ height: '70px', objectFit: 'contain', filter: 'drop-shadow(0 0 14px ' + brandColor + '88)' }}
                    onError={e => { e.target.style.display = 'none'; }} />
                  <div style={{ ...S.anton, fontSize: '1rem', letterSpacing: '2px', color: brandColor, textAlign: 'center' }}>{selectedPLEData.name.toUpperCase()}</div>
                  {isWrestleManiaBlock && <div style={{ ...S.mono, fontSize: '0.42rem', color: '#d4af37', letterSpacing: '1px' }}>⭐ FORCED</div>}
                  {!isWrestleManiaBlock && !canChangePLE && <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)' }}>IN {weeksLeft} WEEK{weeksLeft > 1 ? 'S' : ''}</div>}
                  {canChangePLE && <div style={{ ...S.mono, fontSize: '0.42rem', color: 'rgba(255,255,255,0.25)', letterSpacing: '1px' }}>✏ CLICK TO CHANGE</div>}
                </>
              ) : (
                <>
                  <div style={{ width: '70px', height: '70px', border: '1px dashed rgba(255,255,255,0.15)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '1.5rem' }}>+</span>
                  </div>
                  <div style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>SELECT NEXT PLE</div>
                </>
              )}
            </div>
          </div>

          {/* PLE Picker popup */}
          {showPLEPicker && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)' }}>
              <div style={{ background: 'rgba(6,4,0,0.97)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '14px', width: '100%', maxWidth: '580px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ ...S.anton, fontSize: '1rem', letterSpacing: '3px', color: '#d4af37' }}>SELECT NEXT PLE</div>
                  <button onClick={() => setShowPLEPicker(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
                </div>
                <div style={{ padding: '16px 24px', overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {PLES.filter(function(ple) { return ple.id !== 'wrestlemania'; }).map(ple => {
                    const isSel = selectedPLE === ple.id;
                    return (
                      <div key={ple.id} className={'ple-card' + (isSel ? ' selected' : '')}
                        onClick={() => { setSelectedPLE(ple.id); setShowPLEPicker(false); }}
                        style={{ borderRadius: '10px', border: '1px solid ' + (isSel ? brandColor : 'rgba(255,255,255,0.1)'), background: isSel ? brandColor + '18' : 'rgba(255,255,255,0.03)', padding: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <img
                          src={getPLELogo(ple.id)}
                          alt={ple.name}
                          style={{ width: '100%', height: '52px', objectFit: 'contain', filter: isSel ? 'drop-shadow(0 0 8px ' + brandColor + ')' : 'brightness(0.85)' }}
                          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                        />
                        <span style={{ display: 'none', fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: isSel ? brandColor : 'rgba(255,255,255,0.5)', textAlign: 'center' }}>{ple.name}</span>
                        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: isSel ? brandColor : 'rgba(255,255,255,0.4)', textAlign: 'center', letterSpacing: '0.5px' }}>{ple.name}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* CTA */}
          <button className="btn-wwe"
            onClick={() => onStartWeek({ nextPle: selectedPLE, budget: editedBudget })}
            disabled={canChangePLE && !selectedPLE}
            style={{ width: '100%', padding: '16px', fontFamily: "'Anton',sans-serif", fontSize: '1rem', letterSpacing: '4px', background: 'transparent', border: '2px solid ' + (canChangePLE && !selectedPLE ? 'rgba(255,255,255,0.15)' : brandColor), color: canChangePLE && !selectedPLE ? 'rgba(255,255,255,0.25)' : brandColor, borderRadius: '8px', cursor: canChangePLE && !selectedPLE ? 'not-allowed' : 'pointer', transition: 'background 0.2s, border-color 0.2s, color 0.2s', textShadow: canChangePLE && !selectedPLE ? 'none' : '0 0 20px ' + brandColor + '88', boxShadow: canChangePLE && !selectedPLE ? 'none' : '0 0 30px ' + brandColor + '22' }}>
            ▶ START WEEK {week}
          </button>
        </div>

        {/* Bottom accent */}
        <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, ' + brandColor + '44, transparent)' }} />
      </div>
    </div>
  );
}
